import type * as THREE from 'three';
import { Matrix4, Quaternion, Vector3 } from 'three';
import type { Lesson } from '@vrlearning/lesson-schema';
import { LIMITER, fadeSeconds, gainsFor, type Mix, type MixInput } from './mix.js';
import type { SoundPlan } from './plan.js';
import { Soundscape, type SoundscapeOptions } from './soundscape.js';

/**
 * The headset's sound (world audio, ADR-034; design "The audio engine"):
 * **one `AudioContext` for the page**, made the first time it is needed and
 * never closed, like the stage -- a context made later would need another
 * tap, and twenty children would each have to press something between two
 * lessons. Soundscapes come and go with lessons; the context, its buses, the
 * switches' state and the master stay.
 *
 *   world layers ─► ambient bus ─► duck ─► ambient switch ─┐
 *   model sounds ─► models bus ─► models duck ─┘            │
 *   voice files  ─► voice bus ─► voice switch ─────────────┴► master ─► limiter ─► meter ─► speakers
 *
 * It starts suspended: `unlock` resumes it inside a tap ("Start in VR", or the
 * desktop preview's first click or key). Hidden, it fades out and suspends;
 * behind the Quest's system menu it fades out and keeps running.
 */

export type Visibility = 'visible' | 'visible-blurred' | 'hidden';

type Graph = {
  readonly context: AudioContext;
  readonly ambient: GainNode;
  readonly duck: GainNode;
  readonly ambientSwitch: GainNode;
  readonly voice: GainNode;
  readonly voiceSwitch: GainNode;
  readonly master: GainNode;
  readonly limiter: DynamicsCompressorNode;
  readonly meter: AnalyserNode;
  /** The library's models (ADR-043): their own duck, then the ambient switch. */
  readonly models: GainNode;
  readonly modelsDuck: GainNode;
};

/** What `?perf=1` shows: "geluid: 9 stemmen, 3 HRTF", and the master's peak and RMS. */
export type SoundStats = { voices: number; hrtf: number; peakDb: number; rmsDb: number };

/**
 * The plan a lesson plays, if any: its world's, in VR, from the world source the lesson is built from
 * (a package's, or the code world's). The studio, the solar system and AR are silent -- and so is a
 * world lesson without a source: a refused package shows the studio, never the code world's sound.
 */
export function soundPlanFor(
  lesson: Pick<Lesson, 'mode' | 'scene'>,
  source?: { readonly definition: { readonly sound: SoundPlan } },
): SoundPlan | undefined {
  if (lesson.mode === 'ar' || lesson.scene.type !== 'world') return undefined;
  return source?.definition.sound;
}

export class AudioEngine {
  readonly #create: () => AudioContext;
  #graph: Graph | undefined;
  #input: MixInput;
  /** The page's visibility, and the XR session's while there is one (it wins). */
  #page: Visibility = 'visible';
  #xr: Visibility | undefined;
  #visibility: Visibility = 'visible';
  /** Where each gain is heading: read back, a param's value lags (and stands still while suspended). */
  readonly #targets = new Map<AudioParam, number>();
  #soundscape: Soundscape | undefined;
  #suspendTimer: ReturnType<typeof setTimeout> | undefined;
  readonly #unlockedHandlers = new Set<() => void>();
  readonly #inputHandlers = new Set<(input: MixInput) => void>();
  // Written once a frame without allocating (the listener) or twice a second (the meter).
  readonly #matrix = new Matrix4();
  readonly #position = new Vector3();
  readonly #rotation = new Quaternion();
  readonly #scale = new Vector3();
  readonly #forward = new Vector3();
  readonly #up = new Vector3();
  #samples: Float32Array<ArrayBuffer> | undefined;

  constructor(create: () => AudioContext, options: { muted?: boolean } = {}) {
    this.#create = create;
    this.#input = {
      ambient: true,
      voice: true,
      paused: false,
      speaking: false,
      localMute: options.muted === true,
    };
  }

  /** The context, once something needed it. */
  get context(): AudioContext | undefined {
    return this.#graph?.context;
  }

  /** Running: a tap has let it play. */
  get unlocked(): boolean {
    return this.#graph?.context.state === 'running';
  }

  get localMute(): boolean {
    return this.#input.localMute;
  }

  get soundscape(): Soundscape | undefined {
    return this.#soundscape;
  }

  /**
   * The class's switches, the pause and the viewer's mute as they stand: kept
   * across lessons, so a lesson that starts after the teacher switched
   * "Voorlezen" off (a late joiner) starts with it off.
   */
  get input(): MixInput {
    return this.#input;
  }

  /** Told each time the class's switches, the pause or the viewer's mute change. */
  onInput(handler: (input: MixInput) => void): () => void {
    this.#inputHandlers.add(handler);
    return () => this.#inputHandlers.delete(handler);
  }

  /**
   * Where a voice file plays (ADR-033): the voice bus, before the teacher's
   * "Voorlezen" switch and the master. Makes the context if nothing has yet
   * (suspended until a tap, as ever), so a file can be decoded before it is asked for.
   */
  voiceOutput(): { context: AudioContext; bus: AudioNode } {
    const graph = this.#ensure();
    return { context: graph.context, bus: graph.voice };
  }

  /**
   * Where a library model's sounds play (ADR-043; render/audio/model-sound.ts): the `models`
   * bus, ducked −6 dB under a voice and −12 dB paused, then the teacher's "Achtergrondgeluid"
   * and the master. Makes the context if nothing has yet, as voiceOutput does.
   */
  modelsOutput(): { context: AudioContext; bus: AudioNode } {
    const graph = this.#ensure();
    return { context: graph.context, bus: graph.models };
  }

  /** Told once, when a tap first lets it play. */
  onUnlocked(handler: () => void): () => void {
    this.#unlockedHandlers.add(handler);
    return () => this.#unlockedHandlers.delete(handler);
  }

  /** Inside a user's tap, before anything else it does (requestSession): lets the context play. */
  unlock(): void {
    const { context } = this.#ensure();
    if (context.state !== 'running' && this.#visibility !== 'hidden') void context.resume();
  }

  /**
   * Without a tap, once a browser's scene has loaded (Pieter, 29 Sep 2026):
   * asks the context to play. Most browsers want a click or key first and say
   * no, quietly -- the first one does it then (controls.ts) -- but one the
   * viewer has already clicked in lets it play now, and `onUnlocked` says so.
   * Never in a headset, where "Start in VR" is the tap.
   */
  tryStart(): void {
    const { context } = this.#ensure();
    if (context.state === 'running' || this.#visibility === 'hidden') return;
    context.resume().catch(() => {
      // Refused without a gesture: the first click or key lets it play.
    });
  }

  /** The class's switches and pause, as the session says (both on when it does not). */
  setSession(session: { ambient: boolean; voice: boolean; paused: boolean }): void {
    this.#input = { ...this.#input, ...session };
    this.#apply();
    this.#tell();
  }

  /** The viewer's own mute: the demo's and a played lesson's button, `M`, `?sound=0`. */
  setLocalMute(muted: boolean): void {
    this.#input = { ...this.#input, localMute: muted };
    this.#apply();
    this.#tell();
  }

  #tell(): void {
    for (const handler of [...this.#inputHandlers]) handler(this.#input);
  }

  /** A voice file playing (ADR-033): the ambience ducks under it (the voice player's start and end). */
  setSpeaking(speaking: boolean): void {
    if (speaking === this.#input.speaking) return;
    this.#input = { ...this.#input, speaking };
    // At the voice's pace both ways: down in 150 ms, back over 600 ms.
    this.#apply(undefined, 'speaking');
  }

  /**
   * The page hidden (or the headset taken off): fade out in 100 ms and suspend.
   * Behind the Quest's system menu: fade out, keep running. Back: resume and
   * fade in over a second. While an XR session runs its visibility is the one
   * that counts (a browser may hide the page behind immersive mode); `undefined`
   * from `xr` is the session gone.
   */
  setVisibility(visibility: Visibility | undefined, source: 'page' | 'xr' = 'page'): void {
    if (source === 'xr') this.#xr = visibility;
    else if (visibility) this.#page = visibility;
    const effective = this.#xr ?? this.#page;
    if (effective === this.#visibility) return;
    this.#visibility = effective;
    const graph = this.#graph;
    if (!graph) return;
    if (this.#suspendTimer !== undefined) clearTimeout(this.#suspendTimer);
    this.#suspendTimer = undefined;
    if (effective === 'hidden') {
      this.#apply(0.1);
      this.#suspendTimer = setTimeout(() => void graph.context.suspend(), 120);
    } else if (effective === 'visible-blurred') {
      this.#apply(0.1);
    } else {
      // Always: a suspend still under way has not changed `state` yet.
      void graph.context.resume();
      this.#apply(1);
    }
  }

  /** A lesson's world sound, under the ambient bus; whatever played before is taken down. */
  play(options: SoundscapeOptions): Soundscape {
    this.stop();
    const graph = this.#ensure();
    this.#soundscape = new Soundscape(graph.context, graph.ambient, options);
    return this.#soundscape;
  }

  /** Takes the playing soundscape down, in the fade to black. */
  stop(seconds = 0.3): void {
    this.#soundscape?.dispose(seconds);
    this.#soundscape = undefined;
  }

  /**
   * Where the ears are: once a frame, from the camera that tracks the head,
   * as plain values (nothing allocated; each set replaces the last).
   */
  listen(camera: THREE.Object3D, options: { models?: boolean } = {}): void {
    const graph = this.#graph;
    // A world's soundscape, or a library model's sounds (ADR-043), which a studio lesson may have.
    if (!graph || (!this.#soundscape && options.models !== true)) return;
    this.#matrix.copy(camera.matrixWorld);
    this.#matrix.decompose(this.#position, this.#rotation, this.#scale);
    this.#forward.set(0, 0, -1).applyQuaternion(this.#rotation);
    this.#up.set(0, 1, 0).applyQuaternion(this.#rotation);
    const listener = graph.context.listener;
    if (listener.positionX) {
      listener.positionX.value = this.#position.x;
      listener.positionY.value = this.#position.y;
      listener.positionZ.value = this.#position.z;
      listener.forwardX.value = this.#forward.x;
      listener.forwardY.value = this.#forward.y;
      listener.forwardZ.value = this.#forward.z;
      listener.upX.value = this.#up.x;
      listener.upY.value = this.#up.y;
      listener.upZ.value = this.#up.z;
    } else {
      // Older browsers: the deprecated setters.
      const old = listener as unknown as {
        setPosition(x: number, y: number, z: number): void;
        setOrientation(x: number, y: number, z: number, ux: number, uy: number, uz: number): void;
      };
      old.setPosition(this.#position.x, this.#position.y, this.#position.z);
      old.setOrientation(
        this.#forward.x,
        this.#forward.y,
        this.#forward.z,
        this.#up.x,
        this.#up.y,
        this.#up.z,
      );
    }
  }

  /** For `?perf=1`: sources, HRTF panners, and the master's peak and RMS (dBFS) now. */
  stats(): SoundStats {
    const meter = this.#graph?.meter;
    let peak = 0;
    let sum = 0;
    if (meter) {
      this.#samples ??= new Float32Array(new ArrayBuffer(meter.fftSize * 4));
      meter.getFloatTimeDomainData(this.#samples);
      for (const value of this.#samples) {
        peak = Math.max(peak, Math.abs(value));
        sum += value * value;
      }
    }
    const count = this.#samples?.length ?? 1;
    return {
      voices: this.#soundscape?.voices ?? 0,
      hrtf: this.#soundscape?.hrtf ?? 0,
      peakDb: 20 * Math.log10(peak + 1e-9),
      rmsDb: 10 * Math.log10(sum / count + 1e-18),
    };
  }

  /** The mix now: what the gains are heading for. */
  get mix(): Mix {
    return this.#mix();
  }

  #mix(): Mix {
    const mix = gainsFor(this.#input);
    return this.#visibility === 'visible' ? mix : { ...mix, master: 0 };
  }

  #ensure(): Graph {
    if (this.#graph) return this.#graph;
    const context = this.#create();
    const gain = (): GainNode => context.createGain();
    const graph: Graph = {
      context,
      ambient: gain(),
      duck: gain(),
      ambientSwitch: gain(),
      voice: gain(),
      voiceSwitch: gain(),
      master: gain(),
      limiter: context.createDynamicsCompressor(),
      meter: context.createAnalyser(),
      models: gain(),
      modelsDuck: gain(),
    };
    graph.ambient.connect(graph.duck).connect(graph.ambientSwitch).connect(graph.master);
    graph.models.connect(graph.modelsDuck).connect(graph.ambientSwitch);
    graph.voice.connect(graph.voiceSwitch).connect(graph.master);
    graph.limiter.threshold.value = LIMITER.threshold;
    graph.limiter.ratio.value = LIMITER.ratio;
    graph.limiter.knee.value = LIMITER.knee;
    graph.limiter.attack.value = LIMITER.attack;
    graph.limiter.release.value = LIMITER.release;
    graph.meter.fftSize = 2048;
    graph.master.connect(graph.limiter).connect(graph.meter).connect(context.destination);
    const mix = this.#mix();
    graph.duck.gain.value = mix.duck;
    graph.modelsDuck.gain.value = mix.models;
    graph.ambientSwitch.gain.value = mix.ambient;
    graph.voiceSwitch.gain.value = mix.voice;
    graph.master.gain.value = mix.master;
    for (const [param, value] of [
      [graph.duck.gain, mix.duck],
      [graph.modelsDuck.gain, mix.models],
      [graph.ambientSwitch.gain, mix.ambient],
      [graph.voiceSwitch.gain, mix.voice],
      [graph.master.gain, mix.master],
    ] as const)
      this.#targets.set(param, value);
    context.onstatechange = () => {
      if (context.state !== 'running') return;
      for (const handler of [...this.#unlockedHandlers]) {
        this.#unlockedHandlers.delete(handler);
        handler();
      }
    };
    this.#graph = graph;
    return graph;
  }

  /**
   * Every gain towards the mix, each at its own pace; `master` overrides the
   * master's, `duckBy` says whose pace the duck moves at (a voice's, or the pause's).
   */
  #apply(
    masterSeconds?: number,
    duckBy: 'speaking' | 'paused' = this.#input.speaking ? 'speaking' : 'paused',
  ): void {
    const graph = this.#graph;
    if (!graph) return;
    const mix = this.#mix();
    const now = graph.context.currentTime;
    const move = (param: AudioParam, to: number, seconds: number): void => {
      if ((this.#targets.get(param) ?? param.value) === to) return;
      this.#targets.set(param, to);
      param.cancelScheduledValues(now);
      param.setValueAtTime(param.value, now);
      param.linearRampToValueAtTime(to, now + seconds);
    };
    const duck = graph.duck.gain;
    move(duck, mix.duck, fadeSeconds(duckBy, duck.value, mix.duck));
    const models = graph.modelsDuck.gain;
    move(models, mix.models, fadeSeconds(duckBy, models.value, mix.models));
    move(
      graph.ambientSwitch.gain,
      mix.ambient,
      fadeSeconds('switch', graph.ambientSwitch.gain.value, mix.ambient),
    );
    move(
      graph.voiceSwitch.gain,
      mix.voice,
      fadeSeconds('switch', graph.voiceSwitch.gain.value, mix.voice),
    );
    move(
      graph.master.gain,
      mix.master,
      masterSeconds ?? fadeSeconds('master', graph.master.gain.value, mix.master),
    );
  }
}
